import React, { memo, useState } from 'react';
import { Handle, Position, NodeProps, Node, useEdges } from '@xyflow/react';
import { ServiceData, NodeStatus, NetworkZone, ConnectorData } from '../types/diagram.types';
import { GROUP_COLORS } from '../constants/groupColors';
import { CONNECTOR_STYLES } from '../constants/connectorStyles';

type ServiceNodeType = Node<ServiceData, 'service'>;

const STATUS_COLORS: Record<NodeStatus, string> = {
  active: '#4CAF50',
  deprecated: '#9E9E9E',
  planned: '#2196F3',
};

const ZONE_INFO: Record<NetworkZone, { num: number; color: string; label: string }> = {
  'zone-external': { num: 1, color: '#757575', label: 'Зона 1 — Внешняя' },
  'zone-dmz':      { num: 2, color: '#E65100', label: 'Зона 2 — DMZ' },
  'zone-kafka':    { num: 5, color: '#FF8F00', label: 'Зона 5 — Kafka' },
  'zone-internal': { num: 3, color: '#1565C0', label: 'Зона 3 — Внутренний контур' },
  'zone-partners': { num: 4, color: '#6A1B9A', label: 'Зона 4 — Партнёры' },
};

const DB_ICONS: Record<string, string> = {
  PostgreSQL: 'PG', Redis: 'RD', TimescaleDB: 'TS', S3: 'S3', Kafka: 'KF',
};

const ServiceNode = memo(({ data, selected, id }: NodeProps<ServiceNodeType>) => {
  const [expanded, setExpanded] = useState(false);
  const edges = useEdges();

  const groupColor = GROUP_COLORS[data.group] ?? '#546E7A';
  const statusColor = STATUS_COLORS[data.status] ?? STATUS_COLORS.active;
  const zoneInfo = ZONE_INFO[data.zone as NetworkZone] ?? ZONE_INFO['zone-internal'];

  const myEdges = edges.filter(e => e.source === id || e.target === id);


  return (
    <div
      style={{
        background: '#fff',
        border: selected ? `2px solid ${groupColor}` : '1.5px solid #E0E0E0',
        borderRadius: 8,
        boxShadow: selected ? `0 4px 16px ${groupColor}44` : '0 2px 8px rgba(0,0,0,0.1)',
        minWidth: expanded ? 280 : 170,
        maxWidth: expanded ? 320 : 210,
        fontFamily: 'system-ui, sans-serif',
        overflow: 'visible',
        transition: 'box-shadow 0.15s, border-color 0.15s, min-width 0.2s',
      }}
    >
      {/* Header */}
      <div style={{ background: groupColor, padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 6, minHeight: 30, borderRadius: '6px 6px 0 0' }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor, border: '1.5px solid rgba(255,255,255,0.6)', flexShrink: 0 }} title={data.status} />
        <span style={{ color: '#fff', fontSize: 11, fontWeight: 700, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={data.name}>
          {data.name}
        </span>
        {data.version && (
          <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.75)', background: 'rgba(0,0,0,0.2)', borderRadius: 3, padding: '1px 4px', flexShrink: 0 }}>
            {data.version}
          </span>
        )}
        <span
          title={zoneInfo.label}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: zoneInfo.color,
            color: '#fff',
            fontSize: 9,
            fontWeight: 900,
            border: '1.5px solid rgba(255,255,255,0.45)',
            flexShrink: 0,
            letterSpacing: 0,
          }}
        >
          {zoneInfo.num}
        </span>
      </div>

      {/* Body */}
      <div style={{ padding: '5px 10px 6px' }}>
        <div style={{ fontSize: 10, color: '#757575', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {data.group}
        </div>

        {/* DB badges */}
        {data.databases && data.databases.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 5 }}>
            {data.databases.map(db => (
              <span key={db} style={{ fontSize: 9, fontWeight: 600, padding: '1px 5px', borderRadius: 3, background: '#F5F5F5', color: '#546E7A', border: '1px solid #E0E0E0' }}>
                {DB_ICONS[db] ?? db}
              </span>
            ))}
          </div>
        )}

        {/* Pin counts + expand button */}
        {myEdges.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
            <span style={{ fontSize: 10, color: '#757575' }}>
              <span style={{ color: '#2196F3', fontWeight: 700 }}>{myEdges.filter(e => e.target === id).length}</span>
              <span style={{ fontSize: 9 }}> &larr; </span>
              <span style={{ color: '#4CAF50', fontWeight: 700 }}>{myEdges.filter(e => e.source === id).length}</span>
              <span style={{ fontSize: 9 }}> &rarr; </span>
              <span style={{ color: '#9E9E9E' }}>({myEdges.length})</span>
            </span>
            <button
              onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
              style={{
                marginLeft: 'auto',
                fontSize: 9,
                padding: '1px 5px',
                borderRadius: 3,
                border: `1px solid ${groupColor}88`,
                background: `${groupColor}12`,
                color: groupColor,
                cursor: 'pointer',
                fontWeight: 700,
              }}
            >
              {expanded ? '▲ hide' : '▼ pins'}
            </button>
          </div>
        )}

        {/* Expanded connector list with numbering */}
        {expanded && myEdges.length > 0 && (
          <div style={{ marginTop: 6, borderTop: '1px solid #F0F0F0', paddingTop: 5 }}>
            {myEdges.map(e => {
              const isIn = e.target === id;
              const eData = e.data as ConnectorData;
              const cs = CONNECTOR_STYLES[eData?.type ?? 'REST'] ?? CONNECTOR_STYLES.REST;
              return (
                <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 0', borderBottom: '1px solid #F8F8F8' }}>
                  <span style={{ color: '#BDBDBD', minWidth: 22, fontSize: 9, fontWeight: 700, textAlign: 'right', flexShrink: 0 }}>
                    #{eData?.index ?? '?'}
                  </span>
                  <span style={{
                    background: cs.color, color: '#fff',
                    padding: '1px 4px', borderRadius: 3, fontSize: 9, fontWeight: 700, flexShrink: 0,
                  }}>
                    {cs.label}
                  </span>
                  <span style={{ color: '#757575', fontSize: 10, flexShrink: 0 }}>{isIn ? '←' : '→'}</span>
                  <span style={{ fontSize: 10, color: '#424242', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={eData?.label ?? e.id}>
                    {eData?.label ?? e.id}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Handles — zone-aware layout
          Z1 external  : bottom only
          Z2 DMZ       : top (←→Z1) + bottom (←→Z3)
          Z3 internal  : top (←→Z1,Z2) + bottom (←→Z4) + left/right (←→Z3)
          Z4 partners  : top only
      */}
      {(() => {
        const zone = data.zone as NetworkZone | undefined;
        const tgt  = { background: '#90A4AE', border: '2px solid #fff', width: 10, height: 10 };
        const tgtS = { background: '#90A4AE', border: '2px solid #fff', width: 8, height: 8 };
        const src  = { background: groupColor, border: '2px solid #fff', width: 10, height: 10 };
        const srcS = { background: groupColor, border: '2px solid #fff', width: 8, height: 8 };

        if (zone === 'zone-external') {
          // Z1: коннекты только снизу
          return (
            <>
              <Handle type="target" position={Position.Bottom} id="bottom-in" style={tgtS} />
              <Handle type="source" position={Position.Bottom} id="bottom"    style={{ ...srcS, marginLeft: 14 }} />
            </>
          );
        }
        if (zone === 'zone-dmz') {
          // Z2: сверху — для Z1, снизу — для Z3
          return (
            <>
              <Handle type="target" position={Position.Top}    id="top"       style={tgtS} />
              <Handle type="source" position={Position.Top}    id="top-out"   style={{ ...srcS, marginLeft: 14 }} />
              <Handle type="target" position={Position.Bottom} id="bottom-in" style={tgtS} />
              <Handle type="source" position={Position.Bottom} id="bottom"    style={{ ...srcS, marginLeft: 14 }} />
            </>
          );
        }
        if (zone === 'zone-partners') {
          // Z4: коннекты только сверху
          return (
            <>
              <Handle type="target" position={Position.Top} id="top"     style={tgtS} />
              <Handle type="source" position={Position.Top} id="top-out" style={{ ...srcS, marginLeft: 14 }} />
            </>
          );
        }
        // Z3 internal: сверху (→Z1/Z2), снизу (→Z4), слева/справа (→Z3)
        return (
          <>
            <Handle type="target" position={Position.Left}   id="left"      style={tgt} />
            <Handle type="source" position={Position.Right}  id="right"     style={src} />
            <Handle type="target" position={Position.Top}    id="top"       style={tgtS} />
            <Handle type="source" position={Position.Top}    id="top-out"   style={{ ...srcS, marginLeft: 14 }} />
            <Handle type="source" position={Position.Bottom} id="bottom"    style={srcS} />
            <Handle type="target" position={Position.Bottom} id="bottom-in" style={{ ...tgtS, marginLeft: 14 }} />
          </>
        );
      })()}
    </div>
  );
});

ServiceNode.displayName = 'ServiceNode';
export default ServiceNode;
